import React, { ReactNode, useCallback } from "react";

import { namespace } from "@formant/core/common/config";
import { Button } from "@formant/ui-kit";

import { useStores } from "../context/StoreContext";
import logomarkSvg from "../static/images/logomark.svg";
import { reload } from "../utils/reload";
import { Heading1 } from "./common/Heading1";
import { IconButton } from "./common/IconButton";
import { TextButton } from "./common/TextButton";
import * as styles from "./ErrorPage.scss";
import { ErrorPageError } from "./ErrorPageError";

export interface IErrorPageProps {
    error?: Error;
    title?: string;
    message?: string;
    reload?: boolean;
    logout?: boolean;
    menu?: ReactNode;
    back?: boolean;
    children?: React.ReactNode;
}

export function ErrorPage(props: IErrorPageProps) {
    const {
        children,
        logout,
        menu = false,
        back: goBack,
        title = "Oh snap",
        reload: canReload = true,
    } = props;

    const { appStore, authStore, routerStore } = useStores();

    const onLogout = useCallback(() => {
        authStore.logout();
        window.location.href = "/";
    }, [authStore]);

    const onMenu = useCallback(() => {
        appStore.sidebarIsOpen = true;
    }, [appStore]);

    const onBack = useCallback(
        async () => await routerStore.back(),
        [routerStore],
    );

    const message =
        props.message ||
        (props.error instanceof ErrorPageError &&
            (props.error as ErrorPageError).message) ||
        "An error occurred.";

    const error =
        (props.error instanceof ErrorPageError &&
            (props.error as ErrorPageError).cause) ||
        props.error;

    return (
        <div className={styles["error-page"]}>
            <img className={styles.logo} src={logomarkSvg} />
            <div className={styles["fixed-size-container"]}>
                <div className={styles["error-container"]}>
                    <Heading1>{title}</Heading1>
                    {menu && (
                        <IconButton
                            icon="menu"
                            onClicked={onMenu}
                            className={styles.menu}
                        />
                    )}
                    <p>{message}</p>
                    {error && namespace !== "prod" && (
                        <code>{error.toString()}</code>
                    )}
                    {children}
                    {canReload && (
                        <Button
                            variant="default"
                            size="lg"
                            onClick={reload}
                            className={styles.button}
                        >
                            Refresh
                        </Button>
                    )}
                    {goBack && (
                        <Button
                            variant="default"
                            size="lg"
                            onClick={onBack}
                            className={styles.button}
                        >
                            Go Back
                        </Button>
                    )}
                    {logout !== false && authStore.authenticated && (
                        <TextButton
                            onClicked={onLogout}
                            className={styles.button}
                        >
                            Sign Out
                        </TextButton>
                    )}
                </div>
            </div>
            {menu}
        </div>
    );
}
